import { AlertProps } from '@mui/material/Alert';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import clusterApi from '~/api/v2/endpoints/cluster';
import datasetApi from '~/api/v2/endpoints/dataset';
import datasetAccessApi from '~/api/v2/endpoints/datasetAccess';
import emrApi from '~/api/v2/endpoints/emr';
import etlJobsApi from '~/api/v2/endpoints/etlJobs';
import memberApi from '~/api/v2/endpoints/member';
import noticeBannerApi from '~/api/v2/endpoints/noticeBanner';
import projectApi from '~/api/v2/endpoints/project';
import serviceNowApi from '~/api/v2/endpoints/serviceNow';
import storageApi from '~/api/v2/endpoints/storage';
import workbenchApi from '~/api/v2/endpoints/workbench';
import { ApiErrorData, ApiErrorDetail, ApiErrorResponse } from '~/api/v2/v2Api';

interface AlertModel {
  message: string;
  type: AlertProps['severity'];
  id: string;
}

const getApiErrorMessage = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const data = (payload as ApiErrorResponse).data as ApiErrorData | undefined;
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  const { detail } = data;
  if (typeof detail === 'string') {
    return detail;
  }

  if (detail && typeof detail === 'object') {
    const { message } = detail as ApiErrorDetail;
    if (typeof message === 'string') {
      return message;
    }
  }

  return undefined;
};

const alertSlice = createSlice({
  name: 'alert',
  initialState: [] as AlertModel[],
  reducers: {
    removeAlert: (state, { payload }: PayloadAction<string>) => {
      return state.filter((alert) => alert.id !== payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(emrApi.endpoints.createEmr.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `A studio ${action.meta.arg.originalArgs.name!} has been successfully created.`,
          type: 'success',
        });
      })
      .addMatcher(emrApi.endpoints.createEmr.matchFulfilled, (state, action) => {
        if (action?.payload?.data?.warnings?.length) {
          state.push({
            id: crypto.randomUUID(),
            message: action?.payload?.data?.warnings.join(', '),
            type: 'warning',
          });
        }
      })
      .addMatcher(projectApi.endpoints.deactivateProject.matchFulfilled, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Project has been successfully deactivated.`,
          type: 'success',
        });
      })
      .addMatcher(projectApi.endpoints.deactivateProject.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Project deactivation failed.`,
          type: 'error',
        });
      })
      .addMatcher(emrApi.endpoints.createEmr.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Failed to create “${action.meta.arg.originalArgs.name!}”`,
          type: 'error',
        });
      })
      .addMatcher(emrApi.endpoints.editEmr.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `A studio ${action.meta.arg.originalArgs.name!} has been successfully edited.`,
          type: 'success',
        });
      })
      .addMatcher(emrApi.endpoints.editEmr.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Failed to edit “${action.meta.arg.originalArgs.name!}”`,
          type: 'error',
        });
      })
      .addMatcher(clusterApi.endpoints.createCluster.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `A Cluster ${action.meta.arg.originalArgs.name!} has been successfully created.`,
          type: 'success',
        });
      })
      .addMatcher(clusterApi.endpoints.createCluster.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Failed to create “${action.meta.arg.originalArgs.name!}”`,
          type: 'error',
        });
      })
      .addMatcher(projectApi.endpoints.createProject.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Project “${action.meta.arg.originalArgs.name!}” was successfully created`,
          type: 'success',
        });
      })
      .addMatcher(projectApi.endpoints.createProject.matchRejected, (state, action) => {
        if (action?.payload?.status === 409) {
          state.push({
            id: crypto.randomUUID(),
            message: `Project “${action.meta.arg.originalArgs.name!}” already exists`,
            type: 'error',
          });
        } else {
          state.push({
            id: crypto.randomUUID(),
            message: `Project “${action.meta.arg.originalArgs.name!}” creation failed`,
            type: 'error',
          });
        }
      })
      .addMatcher(datasetApi.endpoints.uploadDatasetFiles.matchFulfilled, (state, action) => {
        const filesUploadResult = action.payload.reduce(
          (acc, result) => {
            if (result.status === 'fulfilled') {
              acc.fulfilled.push(result.value.file.name);
            } else if (result.status === 'rejected') {
              acc.rejected.push(result.reason.name);
            }

            return acc;
          },
          { rejected: [] as string[], fulfilled: [] as string[] },
        );
        const isFulfilledSingular = filesUploadResult.fulfilled.length === 1;
        const isRejectedSingular = filesUploadResult.rejected.length === 1;
        if (filesUploadResult.fulfilled.length > 0) {
          state.push({
            id: crypto.randomUUID(),
            message: `${isFulfilledSingular ? 'File' : 'Files'} "${filesUploadResult.fulfilled.join()}” ${isRejectedSingular ? 'was' : 'were'}  successfully uploaded`,
            type: 'success',
          });
        }
        if (filesUploadResult.rejected.length > 0) {
          state.push({
            id: crypto.randomUUID(),
            message: `${isRejectedSingular ? 'File' : 'Files'} "${filesUploadResult.rejected.join()}” failed during upload`,
            type: 'error',
          });
        }
      })
      .addMatcher(datasetApi.endpoints.createDataset.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Dataset “${action.meta.arg.originalArgs.name!}” was successfully created`,
          type: 'success',
        });
      })
      .addMatcher(datasetApi.endpoints.editDataset.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Dataset “${action.meta.arg.originalArgs.name!}” was successfully edited`,
          type: 'success',
        });
      })
      .addMatcher(noticeBannerApi.endpoints.deleteNoticeBanner.matchFulfilled, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Notice Banner was successfully deleted`,
          type: 'success',
        });
      })
      .addMatcher(noticeBannerApi.endpoints.deleteNoticeBanner.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: getApiErrorMessage(action.payload) || `Notice Banner deletion failed`,
          type: 'error',
        });
      })
      .addMatcher(noticeBannerApi.endpoints.editNoticeBanner.matchFulfilled, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Notice Banner was successfully edited`,
          type: 'success',
        });
      })
      .addMatcher(noticeBannerApi.endpoints.editNoticeBanner.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: getApiErrorMessage(action.payload) || `Notice Banner editing failed`,
          type: 'error',
        });
      })
      .addMatcher(noticeBannerApi.endpoints.createNoticeBanner.matchFulfilled, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Notice Banner was successfully created`,
          type: 'success',
        });
      })
      .addMatcher(noticeBannerApi.endpoints.createNoticeBanner.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: getApiErrorMessage(action.payload) || `Notice Banner creation failed`,
          type: 'error',
        });
      })
      .addMatcher(datasetApi.endpoints.sendDatasetToAtlan.matchFulfilled, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: `The dataset is successfully published.`,
          type: 'success',
        });
      })
      .addMatcher(datasetApi.endpoints.sendDatasetToAtlan.matchPending, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Publishing dataset to Altan has successfully started.`,
          type: 'success',
        });
      })
      .addMatcher(datasetApi.endpoints.sendDatasetToAtlan.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: `The dataset publishing failed.`,
          type: 'error',
        });
      })
      .addMatcher(storageApi.endpoints.createFolder.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Folder “${action.meta.arg.originalArgs.folder_path.split('/').pop()}” was successfully created`,
          type: 'success',
        });
      })
      .addMatcher(projectApi.endpoints.editProject.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message:
            getApiErrorMessage(action.payload) || `Project “${action.meta.arg.originalArgs.name!}” changes failed`,
          type: 'error',
        });
      })
      .addMatcher(projectApi.endpoints.editProject.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Project “${action.meta.arg.originalArgs.name!}” changes were successfully applied`,
          type: 'success',
        });
      })
      .addMatcher(memberApi.endpoints.transferOwnership.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: getApiErrorMessage(action.payload) || 'Failed to transfer project ownership',
          type: 'error',
        });
      })
      .addMatcher(memberApi.endpoints.addMember.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to add member',
          type: 'error',
        });
      })
      .addMatcher(memberApi.endpoints.addMember.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `${action?.payload?.data?.user_id} was successfully added`,
          type: 'success',
        });
      })
      .addMatcher(memberApi.endpoints.addMember.matchFulfilled, (state, action) => {
        if (action?.payload?.data?.emr_warning) {
          state.push({
            id: crypto.randomUUID(),
            message: action?.payload?.data?.emr_warning,
            type: 'warning',
          });
        }
      })
      .addMatcher(memberApi.endpoints.removeMember.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to remove member',
          type: 'error',
        });
      })
      .addMatcher(memberApi.endpoints.removeMember.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `${action?.payload?.data?.user_id} was successfully removed`,
          type: 'success',
        });
      })
      .addMatcher(memberApi.endpoints.removeMember.matchFulfilled, (state, action) => {
        if (action?.payload?.data?.emr_warning) {
          state.push({
            id: crypto.randomUUID(),
            message: action?.payload?.data?.emr_warning,
            type: 'warning',
          });
        }
      })
      .addMatcher(workbenchApi.endpoints.createWorkbench.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: getApiErrorMessage(action.payload) || 'Failed to create workbench',
          type: 'error',
        });
      })
      .addMatcher(workbenchApi.endpoints.getConnectToWorkbench.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to get workbench connection',
          type: 'error',
        });
      })
      .addMatcher(workbenchApi.endpoints.startWorkbench.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to start workbench',
          type: 'error',
        });
      })
      .addMatcher(workbenchApi.endpoints.stopWorkbench.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to stop workbench',
          type: 'error',
        });
      })
      .addMatcher(workbenchApi.endpoints.terminateWorkbench.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to terminate workbench',
          type: 'error',
        });
      })
      .addMatcher(workbenchApi.endpoints.autoStopWorkbench.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to autostop workbench',
          type: 'error',
        });
      })
      .addMatcher(workbenchApi.endpoints.deleteAutoStopWorkbench.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to remove workbench autostop',
          type: 'error',
        });
      })
      .addMatcher(serviceNowApi.endpoints.requestDataset.matchRejected, (state, action) => {
        const errorMessage =
          action?.payload?.status === 422 ? 'Validation error requesting dataset' : 'Failed to request dataset';
        state.push({
          id: crypto.randomUUID(),
          message: errorMessage,
          type: 'error',
        });
      })
      .addMatcher(serviceNowApi.endpoints.requestDataset.matchFulfilled, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Dataset was successfully requested',
          type: 'success',
        });
      })
      .addMatcher(projectApi.endpoints.getProjects.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to request projects',
          type: 'error',
        });
      })
      .addMatcher(etlJobsApi.endpoints.getETLJobLog.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to request ETL job logs',
          type: 'error',
        });
      })
      .addMatcher(datasetApi.endpoints.getDatasets.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to request datasets',
          type: 'error',
        });
      })
      .addMatcher(datasetApi.endpoints.createDataset.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to create dataset',
          type: 'error',
        });
      })
      .addMatcher(datasetApi.endpoints.editDataset.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to edit dataset',
          type: 'error',
        });
      })
      .addMatcher(datasetApi.endpoints.getDatasetPresignedUrl.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to upload file',
          type: 'error',
        });
      })
      .addMatcher(datasetApi.endpoints.uploadDatasetFiles.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to upload file',
          type: 'error',
        });
      })
      .addMatcher(datasetApi.endpoints.getDatasetFiles.matchRejected, (state, action) => {
        if (action.meta.aborted || action.meta.condition) return;
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to request files',
          type: 'error',
        });
      })
      .addMatcher(datasetApi.endpoints.renameDatasetFile.matchRejected, (state) => {
        state.push({ id: crypto.randomUUID(), message: 'Failed to rename file', type: 'error' });
      })
      .addMatcher(datasetApi.endpoints.renameDatasetFile.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `${action.meta.arg.originalArgs.oldFilePath.split('/').pop()} renamed to ${action.meta.arg.originalArgs.newFilePath.split('/').pop()}`,
          type: 'success',
        });
      })
      .addMatcher(datasetApi.endpoints.deleteDatasetFile.matchRejected, (state) => {
        state.push({ id: crypto.randomUUID(), message: 'Failed to delete file', type: 'error' });
      })
      .addMatcher(datasetApi.endpoints.deleteDatasetFile.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `${action.meta.arg.originalArgs.filePath.split('/').pop()} was deleted successfully`,
          type: 'success',
        });
      })
      .addMatcher(datasetApi.endpoints.downloadDatasetFile.matchRejected, (state) => {
        state.push({ id: crypto.randomUUID(), message: 'Failed to download file', type: 'error' });
      })
      .addMatcher(storageApi.endpoints.createFolder.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: getApiErrorMessage(action.payload) || 'Failed to create folder',
          type: 'error',
        });
      })
      .addMatcher(storageApi.endpoints.deleteFolder.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to delete folder',
          type: 'error',
        });
      })
      .addMatcher(storageApi.endpoints.deleteFolder.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Folder ${action.meta.arg.originalArgs.folder_path.split('/').pop()} was deleted successfully`,
          type: 'success',
        });
      })
      .addMatcher(storageApi.endpoints.deleteFiles.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to delete file',
          type: 'error',
        });
      })
      .addMatcher(storageApi.endpoints.deleteFiles.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: action.meta.arg.originalArgs.file_path_list
            ?.map((el) => `File ${el.split('/').pop()} was deleted successfully`)
            .join(', '),
          type: 'success',
        });
      })
      .addMatcher(storageApi.endpoints.renameFolder.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to rename folder',
          type: 'error',
        });
      })
      .addMatcher(storageApi.endpoints.renameFolder.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `${action.meta.arg.originalArgs.current_path.split('/').pop()} renamed to ${action.meta.arg.originalArgs.new_path.split('/').pop()}`,
          type: 'success',
        });
      })
      .addMatcher(storageApi.endpoints.downloadFile.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Failed to download ${action.meta.arg.originalArgs.filePath} file`,
          type: 'error',
        });
      })
      .addMatcher(storageApi.endpoints.downloadFile.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Successfully downloaded ${action.meta.arg.originalArgs.filePath} file`,
          type: 'success',
        });
      })
      .addMatcher(storageApi.endpoints.renameFile.matchRejected, (state) => {
        state.push({
          id: crypto.randomUUID(),
          message: 'Failed to rename file',
          type: 'error',
        });
      })
      .addMatcher(storageApi.endpoints.renameFile.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `${action.meta.arg.originalArgs.current_path.split('/').pop()} renamed to ${action.meta.arg.originalArgs.new_path.split('/').pop()}`,
          type: 'success',
        });
      })
      .addMatcher(storageApi.endpoints.uploadStorageFiles.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: action.payload?.data
            ?.map(({ message, name }) => (message ? message : `File ${name} uploaded successfully`))
            .join(', '),
          type: 'success',
        });
      })
      .addMatcher(storageApi.endpoints.uploadStorageFiles.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: getApiErrorMessage(action.payload) as string,
          type: 'error',
        });
      })
      .addMatcher(storageApi.endpoints.completeS3Upload.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `File ${action.meta.arg.originalArgs.file_name} uploaded successfully`,
          type: 'success',
        });
      })
      .addMatcher(storageApi.endpoints.completeS3Upload.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `File ${action.meta.arg.originalArgs.file_name} upload failed`,
          type: 'error',
        });
      })
      .addMatcher(storageApi.endpoints.getPresignedUploadUrl.matchRejected, (state, action) => {
        const message = getApiErrorMessage(action.payload) as string;

        if (action?.payload?.status === 409) {
          state.push({
            id: crypto.randomUUID(),
            message,
            type: 'success',
          });
        } else {
          state.push({
            id: crypto.randomUUID(),
            message: message || `File ${action.meta.arg.originalArgs.file_name} upload failed`,
            type: 'error',
          });
        }
      })
      .addMatcher(etlJobsApi.endpoints.deleteETLJob.matchRejected, (state) => {
        state.push({ id: crypto.randomUUID(), message: 'Failed to delete ETL job', type: 'error' });
      })
      .addMatcher(etlJobsApi.endpoints.deleteETLJob.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Job ${action.payload.data.name} was deleted successfully`,
          type: 'success',
        });
      })
      .addMatcher(etlJobsApi.endpoints.createScheduledJob.matchRejected, (state) => {
        state.push({ id: crypto.randomUUID(), message: 'Failed to create ETL job', type: 'error' });
      })
      .addMatcher(etlJobsApi.endpoints.createScheduledJob.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Job ${action.meta.arg.originalArgs.name} was created successfully`,
          type: 'success',
        });
      })
      .addMatcher(etlJobsApi.endpoints.editScheduledJob.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: getApiErrorMessage(action.payload) || 'Failed to edit ETL job',
          type: 'error',
        });
      })
      .addMatcher(etlJobsApi.endpoints.editScheduledJob.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Job ${action.meta.arg.originalArgs.name} was edited successfully`,
          type: 'success',
        });
      })
      .addMatcher(datasetAccessApi.endpoints.revokeDatasetAccess.matchRejected, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message:
            getApiErrorMessage(action.payload) ||
            `Failed to revoke dataset access for ${action.meta.arg.originalArgs.name}`,
          type: 'error',
        });
      })
      .addMatcher(datasetAccessApi.endpoints.revokeDatasetAccess.matchFulfilled, (state, action) => {
        state.push({
          id: crypto.randomUUID(),
          message: `Access for ${action.meta.arg.originalArgs.name} has been revoked`,
          type: 'success',
        });
      });
  },
});

export const { removeAlert } = alertSlice.actions;
export default alertSlice.reducer;
